import type { FC, ReactElement } from 'react';
import React, { cloneElement, useMemo } from 'react';
import cn from 'classnames';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';

import type { TProgressLayout, TChildrenProps } from '../models';

/**
 * Отображает компонент прогресс-бара в дочернем компоненте.
 */
export const ProgressBarLayout: FC<TProgressLayout> = ({
    CallToAction,
    ProgressBar,
    Slogan,
    Text,
    children,
    className,
    field: { placeholder, progressBarValue },
    forwardedRef,
    isSticky,
    progressBar: {
        callToActionFinalText,
        callToActionText,
        initValue = 0,
        maxValue,
        prefixText,
        slogan,
        sloganPostfix,
        theme,
    },
    progressBarFilledValue,
    style,
    styles,
    wrapperClassNamesHelper,
}) => {
    const { bottomText, topText } = useNamedContainers(children);
    const childProps: TChildrenProps = {
        initValue,
        maxValue,
        progressBarFilledValue,
        progressBarValue,
        placeholder,
    };

    const wrapperClassNames = useMemo(
        () => wrapperClassNamesHelper(isSticky),
        [isSticky, wrapperClassNamesHelper]
    );

    const TopSlot = topText ? (
        cloneElement(topText as ReactElement, childProps)
    ) : (
        <Text
            className={cn(styles.external, styles.topText)}
            styler={{ marginTop: 0, marginBottom: 8 }}
            tag="div"
        >
            {prefixText && (
                <Text fontSize={14} kind="caption" styler={{ marginTop: 0 }} tag="span">
                    {prefixText}{' '}
                </Text>
            )}
            {slogan && (
                <Text fontSize={14} styler={{ marginTop: 0 }} tag="span">
                    <Slogan
                        initValue={initValue}
                        maxValue={maxValue}
                        progressBarFilledValue={progressBarFilledValue}
                        slogan={slogan}
                        sloganPostfix={sloganPostfix}
                    />
                </Text>
            )}
        </Text>
    );

    const CallToActionText = CallToAction && (
        <CallToAction
            callToActionFinalText={callToActionFinalText}
            callToActionText={callToActionText}
            maxValue={maxValue}
            placeholder={placeholder}
            progressBarFilledValue={progressBarFilledValue}
            progressBarValue={progressBarValue}
        />
    );

    const BottomSlot = bottomText
        ? cloneElement(bottomText as ReactElement, childProps)
        : CallToActionText;

    return (
        <div
            ref={forwardedRef}
            className={cn(wrapperClassNames, className)}
            data-test-id={isSticky ? 'progress-sticky' : 'progress-default'}
            style={style}
        >
            {TopSlot}

            <ProgressBar
                antValue={maxValue}
                initValue={initValue}
                theme={theme}
                value={progressBarFilledValue}
            />

            {BottomSlot}
        </div>
    );
};
